import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  concat,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  concatMap,
  delay,
  finalize,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { constants } from '../../../constants';
import { AuthService } from '../../auth/services/auth.service';
import { AuthorizedResourcesService } from '../../auth/services/authorized-resources.service';
import { AgeGenderCompositionRequest } from '../models/age-gender-composition-request.model';
import { allAgeGroupsLabel } from '../models/age-group.model';
import {
  GroupedResponse,
  GroupedResponseThreeLevels,
  GroupedResponseTwoLevels,
} from '../models/grouped-statistics-response.model';
import { PartialStatisticsResponse } from '../models/partial-statistics-response.model';
import { RangeField } from '../models/range-field.model';
import { RegionSelection } from '../models/region-selection.model';
import { SelectorCountry } from '../models/selector-country.model';
import { SimpleStatisticsResponse } from '../models/simple-statistics-response.model';
import { SpendingDistributionRequest } from '../models/spending-distribution-request';
import { allSpendingGroupsLabel } from '../models/spending-group.model';
import { SpendingStatisticsResponse } from '../models/spending-statistics-response.model';
import { StatisticsRequest } from '../models/statistics-request.model';
import { DataFilters, DataFiltersService } from './data-filters.service';
import { Permissions } from '../../auth/models/permissions.model';
import { IncomeGroup } from '../models/income-group.model';
import { StatisticsService } from './statistics.service';

export class Data<T> {
  public total: T | undefined;
  public current: T | undefined;
}

export class DataFormat {
  countryData: Data<GroupedResponseTwoLevels<SimpleStatisticsResponse>>;
  ageData: Data<GroupedResponseThreeLevels<PartialStatisticsResponse>>;
  spendingDistributionData: Data<
    GroupedResponseThreeLevels<SpendingStatisticsResponse>
  >;
  consumerSpendingData: Data<GroupedResponse<SimpleStatisticsResponse>>;
  worldData: Data<GroupedResponse<SimpleStatisticsResponse>>;
}

@Injectable({
  providedIn: 'root',
})
export class DataService implements OnDestroy {
  private allAuthorizedRegionSelection: RegionSelection = new RegionSelection();
  private areAllCountriesAuthorized: boolean = true;
  private data: BehaviorSubject<DataFormat>;
  private dataFilters: DataFilters = new DataFilters();
  private authDataFilters: DataFilters = new DataFilters();
  private cancelRequests: Subject<void> = new Subject();
  private isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private isLoadingObs: Observable<boolean>;
  private filtersSubscription: Subscription | undefined;
  private localData: any;
  private ngUnsubscribe: Subject<void> = new Subject();
  private permissions: Permissions;

  constructor(
    private http: HttpClient,
    private dataFiltersService: DataFiltersService,
    private authService: AuthService,
    private authResourcesService: AuthorizedResourcesService,
    private statisticsService: StatisticsService
  ) {
    this.data = new BehaviorSubject<DataFormat>({
      countryData: {
        total: undefined,
        current: undefined,
      },
      ageData: {
        total: undefined,
        current: undefined,
      },
      spendingDistributionData: {
        total: undefined,
        current: undefined,
      },
      consumerSpendingData: {
        total: undefined,
        current: undefined,
      },
      worldData: {
        total: undefined,
        current: undefined,
      },
    });
  }
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public init(authFilters: DataFilters = new DataFilters()) {
    if (this.filtersSubscription) {
      return;
    }

    this.allAuthorizedRegionSelection.countries = new Map(
      this.authResourcesService.countries
        .filter((c) => !c.authDisabled)
        .map((c) => {
          const country: SelectorCountry = { ...c };

          if (country.subnational) {
            country.subnational = country.subnational.filter(
              (c) => !c.authDisabled
            );
          }

          return [country.code, country];
        })
    );

    // This enables very important optimization for Elastic
    this.areAllCountriesAuthorized =
      this.authResourcesService.countries.length ===
      this.allAuthorizedRegionSelection.countries.size;

    this.dataFilters = authFilters;
    this.authDataFilters = authFilters;

    // asObservable creates a new observable every time it is called
    // getIsLoading() is being called by the loading.component view periodically
    // so it is better to create the observable and attach debounceTime only once
    this.isLoadingObs = this.isLoading.asObservable().pipe(
      switchMap((isLoading) => {
        const returnObs = of(isLoading);

        return isLoading ? returnObs.pipe(delay(500)) : returnObs;
      })
    );

    // todo: update only total without current data with initial filters, that way we can make the requests parallel
    this.isLoading.next(true);
    combineLatest([
      this.authService.permissions$,
      this.http.get<{ dataVersion: string }>(constants.uriParts.dataVersion),
    ]).subscribe(([permissions, versionObject]) => {
      this.permissions = permissions;
      if (permissions.isFreemium) {
        this.isLoading.next(false);
        return;
      }
      of(this.localData)
        .pipe(
          takeUntil(this.ngUnsubscribe),
          tap((data) => (this.localData = data)),
          concatMap(() => this.inititializeData(false))
        )
        .subscribe(() => {
          if (!this.filtersSubscription) {
            this.filtersSubscription = this.subscribeToDataFiltersChanges();
          }
        });
    });
    this.http
      .get<{ dataVersion: string }>(constants.uriParts.dataVersion)
      .subscribe((versionObject) => {});
  }

  public reset() {
    this.filtersSubscription?.unsubscribe();
    this.filtersSubscription = undefined;
  }

  public getData(): Observable<DataFormat> {
    return this.data.asObservable();
  }

  public getIsLoading(): Observable<boolean> {
    return this.isLoadingObs;
  }

  public getStatisticsDataForRequest<T>(request) {
    return this.http.post<T>(constants.uriParts.statistics, request);
  }

  private subscribeToDataFiltersChanges(): Subscription {
    return this.dataFiltersService
      .valueChanges()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((changedDataFilters) => {
        const lenseSwitched =
          this.dataFilters.dataLens !=
          this.dataFiltersService.getDataFiltersValue().dataLens;
        this.dataFilters = this.dataFiltersService.getDataFiltersValue();

        if (this.isDataFetchNeeded(changedDataFilters)) {
          if (lenseSwitched) {
            this.inititializeData(false)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe();
          } else {
            this.fetchAndUpdateData(false, true, this.dataFilters)
              .pipe(takeUntil(this.ngUnsubscribe))
              .subscribe();
          }
        } else {
          this.data.next(this.data.getValue());
        }
      });
  }

  private inititializeData(cancellable: boolean) {
    const initialFilters: DataFilters = {
      ...new DataFilters(),
      year: this.authDataFilters.year,
      regionSelection: this.authDataFilters.regionSelection,
    };

    return concat(
      this.fetchAndUpdateData(true, cancellable, initialFilters),
      this.fetchAndUpdateData(false, cancellable, this.dataFilters)
    );
  }

  private isDataFetchNeeded(changedDataFilters: Partial<DataFilters>): boolean {
    return (
      changedDataFilters.ageGroup !== undefined ||
      changedDataFilters.dashboard !== undefined ||
      changedDataFilters.dataLens !== undefined ||
      changedDataFilters.gender !== undefined ||
      changedDataFilters.regionSelection !== undefined ||
      changedDataFilters.spendingGroup !== undefined ||
      (changedDataFilters.ageGroup === undefined &&
        changedDataFilters.dashboard === undefined &&
        changedDataFilters.dataLens === undefined &&
        changedDataFilters.gender === undefined &&
        changedDataFilters.regionSelection === undefined &&
        changedDataFilters.spendingGroup === undefined)
    );
  }

  private fetchAndUpdateData(
    updateTotal: boolean,
    cancellable: boolean,
    dataFilters: DataFilters
  ) {
    this.isLoading.next(true);
    this.cancelRequests.next();
    const updateDataFunctions = [
      this.updateData(
        () => this.getSpendingDistributionData(dataFilters),
        'spendingDistributionData',
        updateTotal,
        cancellable
      ),
      this.updateData(
        () => this.getAgeAndGenderCompositionData(dataFilters),
        'ageData',
        updateTotal,
        cancellable
      ),
      this.updateData(
        () => this.getConsumerSpendingData(dataFilters),
        'consumerSpendingData',
        updateTotal,
        cancellable
      ),
      this.updateData(
        () => this.getCountryData(dataFilters),
        'countryData',
        updateTotal,
        cancellable
      ),
      this.updateData(
        () => this.getWorldData(dataFilters),
        'worldData',
        updateTotal,
        cancellable
      ),
    ];

    return forkJoin(updateDataFunctions).pipe(
      finalize(() => this.isLoading.next(false))
    );
  }

  private getWorldData(
    dataFilters: DataFilters
  ): Observable<GroupedResponse<SimpleStatisticsResponse>> {
    return this.getStatisticsData<GroupedResponse<SimpleStatisticsResponse>>(
      ['year'],
      {
        ...dataFilters,
        // Modify filter to get the world, without any countries/states selected
        regionSelection: new RegionSelection(),
      }
    );
  }

  private updateData(
    dataFunc: () => Observable<any>,
    dataKey: string,
    updateTotal: boolean,
    cancellable: boolean
  ) {
    let getDataObs = dataFunc();
    if (cancellable) {
      getDataObs = getDataObs.pipe(takeUntil(this.cancelRequests));
    }

    return getDataObs.pipe(
      tap((data) => {
        let dataToUpdate: any = {
          ...this.data.getValue()[dataKey],
          current: data,
        };
        if (updateTotal) {
          dataToUpdate = { ...dataToUpdate, total: data };
        }
        this.data.next({
          ...this.data.getValue(),
          [dataKey]: dataToUpdate,
        });
      })
    );
  }

  private getAgeAndGenderCompositionData(
    dataFilters: DataFilters
  ): Observable<GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>> {
    if (dataFilters.spendingGroup instanceof IncomeGroup) {
      const request = this.statisticsService.buildRequest();
      return this.http.post<
        GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>
      >(constants.uriParts.statisticsAgeGenderComposition, request);
    }

    const request = new AgeGenderCompositionRequest(
      new RangeField(
        dataFilters.spendingGroup.greaterThan,
        dataFilters.spendingGroup.lessThan
      ),
      this.getRegionCodes(dataFilters),
      this.getSubnationalCode()
    );

    return this.http.post<
      GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>
    >(constants.uriParts.ageGenderComposition, request);
  }

  private getSpendingDistributionData(
    dataFilters: DataFilters
  ): Observable<GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>> {
    if (dataFilters.spendingGroup instanceof IncomeGroup) {
      const request = this.statisticsService.buildRequest();
      return this.http.post<
        GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>
      >(constants.uriParts.statisticsTotalDistribution, request);
    }

    const legacyRequest = new SpendingDistributionRequest(
      new RangeField(
        dataFilters.ageGroup.greaterThan,
        dataFilters.ageGroup.lessThan
      ),
      this.getRegionCodes(dataFilters),
      this.getSubnationalCode()
    );

    return this.http.post<
      GroupedResponseThreeLevels<Partial<SimpleStatisticsResponse>>
    >(constants.uriParts.spendingDistribution, legacyRequest);
  }

  private getConsumerSpendingData(dataFilters: DataFilters) {
    if (dataFilters.spendingGroup instanceof IncomeGroup) {
      const request = this.statisticsService.buildRequest();

      return this.http.post<GroupedResponse<SimpleStatisticsResponse>>(
        constants.uriParts.statisticsConsumerData,
        {
          ...request,
          ...(dataFilters.year !== undefined && {
            annualMarketYears: {
              gte: dataFilters.year,
              lte: dataFilters.year + 1,
            },
          }),
        }
      );
    }

    return this.getStatisticsData<GroupedResponse<SimpleStatisticsResponse>>(
      ['year'],
      dataFilters
    );
  }

  public getCountryData(dataFilters: DataFilters) {
    return this.getStatisticsData<
      GroupedResponseTwoLevels<SimpleStatisticsResponse>
    >(['year', 'state'], {
      ...dataFilters,
      // Modify filter to get not only currently selected, but all authorized countries
      regionSelection: this.getAuthorizedStates(),
    });
  }

  private getStatisticsData<T>(
    groupBy: string[],
    filters: DataFilters
  ): Observable<T> {
    if (filters.spendingGroup instanceof IncomeGroup) {
      return of(undefined as unknown as T);
    }

    const yearRanges = this.permissions?.allowedYears;
    let yearRange = new RangeField(0);
    if (yearRanges?.length) {
      yearRange = new RangeField(
        Math.min(...yearRanges),
        Math.max(...yearRanges)
      );
    }

    const request = new StatisticsRequest(
      yearRange,
      new RangeField(
        filters.spendingGroup.greaterThan,
        filters.spendingGroup.lessThan
      ),
      new RangeField(filters.ageGroup.greaterThan, filters.ageGroup.lessThan),
      this.getRegionCodes(filters),
      filters.gender,
      groupBy,
      [],
      this.getSubnationalCode()
    );

    if (filters.generation) {
      request.generations = [filters.generation];
    }

    return this.getStatisticsDataForRequest<T>(request);
  }

  private getSubnationalCode(): string | undefined {
    if (
      this.dataFilters.dataLens &&
      this.dataFilters.regionSelection.countries.size === 1
    ) {
      return [...this.dataFilters.regionSelection.countries.values()][0].code;
    }

    return undefined;
  }

  private getRegionCodes(dataFilters: DataFilters): string[] {
    return this.getSubnationalCode()
      ? dataFilters.regionSelection.getSubnationalCodes()
      : dataFilters.regionSelection.getCountryCodes();
  }

  private getAuthorizedStates(): RegionSelection {
    let subnationalCode = this.getSubnationalCode();

    if (subnationalCode) {
      let subnationalRegion = new RegionSelection();
      // Only one country is expected to be selected when using subnational.
      subnationalRegion.countries.set(
        subnationalCode,
        this.allAuthorizedRegionSelection.countries.get(subnationalCode)!
      );
      subnationalRegion.subnational = new Map(
        subnationalRegion.countries
          .get(subnationalCode)
          ?.subnational?.map((s) => [s.code, s])
      );

      return subnationalRegion;
    }

    if (this.areAllCountriesAuthorized) {
      // This is very important optimization for Elastic
      // When all contries are requested it is 5-10x slower than no filter for countries
      return new RegionSelection();
    }

    return this.allAuthorizedRegionSelection;
  }

  private getGenderName(gender: string | undefined) {
    if (!gender) {
      return 'null';
    }

    return gender.toLocaleLowerCase();
  }

  private getAgeGroupName(ageGroupCompactName: string): string {
    if (ageGroupCompactName === allAgeGroupsLabel) {
      return 'null';
    }

    return ageGroupCompactName;
  }

  private getSpendingGroupName(spendingGroupCompactName: string): string {
    if (spendingGroupCompactName === allSpendingGroupsLabel) {
      return 'null';
    }

    return spendingGroupCompactName.substring(1);
  }

  private addValues(
    target: Partial<SimpleStatisticsResponse>,
    source: Partial<SimpleStatisticsResponse>
  ) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        // Add to the existing value if it exists, otherwise set the value
        target[key] = (target[key] || 0) + source[key];
      }
    }
  }
}
